/**
 * Resolving a sprite's responsive layout for an orientation (spec 012).
 * The rules are the editor's (`Editor/src/utils/responsive.ts`, which
 * re-exports these): a sprite has one `basis` orientation that holds every
 * field; another orientation stores only the fields it overrides, each
 * falling back to the basis. Layouts without `basis` take the first
 * orientation with a position (portrait, landscape, desktop) as the basis.
 */

import type { DerivedMark, DerivedPart, Orientation, OrientationLayout, Position, ResponsiveLayout, Transform } from '../@types';

export const DEFAULT_POSITION: Readonly<Position> = Object.freeze({ anchor: 'center', x: 50, y: 50, unit: '%' });
export const DEFAULT_TRANSFORM: Readonly<Transform> = Object.freeze({ scale: 1, rotation: 0, flipX: false, flipY: false });

const ORIENTATIONS: Orientation[] = ['portrait', 'landscape', 'desktop'];

/** The basis orientation, falling back to the first orientation with a position. */
export function getBasisOrientation(layout: ResponsiveLayout | undefined): Orientation | undefined {
  if (!layout) return undefined;
  if (layout.basis) return layout.basis;
  return ORIENTATIONS.find((o) => !!layout[o]?.position);
}

function getBasisLayout(layout: ResponsiveLayout | undefined): OrientationLayout | undefined {
  const basis = getBasisOrientation(layout);
  return basis && layout ? layout[basis] : undefined;
}

/** The position in `orientation`: the basis position with that orientation's overridden fields. */
export function getResponsivePosition(layout: ResponsiveLayout | undefined, orientation: Orientation): Position {
  const basisPos = getBasisLayout(layout)?.position ?? DEFAULT_POSITION;
  if (!layout || orientation === getBasisOrientation(layout)) return { ...basisPos };
  const override = layout[orientation]?.position;
  return override ? { ...basisPos, ...override } : { ...basisPos };
}

/** The transform in `orientation`: defaults ← `baseTransform` ← basis transform ← that orientation's overrides. */
export function getResponsiveTransform(
  layout: ResponsiveLayout | undefined,
  orientation: Orientation,
  baseTransform?: Partial<Transform>,
): Transform {
  const base: Transform = { ...DEFAULT_TRANSFORM, ...baseTransform, ...getBasisLayout(layout)?.transform };
  if (!layout || orientation === getBasisOrientation(layout)) return base;
  const override = layout[orientation]?.transform;
  return override ? { ...base, ...override } : base;
}

// ---------------------------------------------------------------------------
// Writes (spec 021: one implementation for the editor store, the
// PropertiesPanel's reset and the scene operations)
// ---------------------------------------------------------------------------

/** Fields that can be independently overridden per orientation. */
export type OverridableField = 'x' | 'y' | 'scale' | 'rotation';

// ---------------------------------------------------------------------------
// The derived mark (spec 021): the one reader and the one writer. Nothing
// else inspects `OrientationLayout.derived`.
// ---------------------------------------------------------------------------

const DERIVED_PARTS: readonly DerivedPart[] = ['position', 'scale', 'rotation'];

/** True when an object mark names a part that is not a `DerivedPart` (v6–v14's retired motion parts). */
const hasRetiredParts = (mark: OrientationLayout['derived']): boolean =>
  typeof mark === 'object' && Object.keys(mark).some((k) => !(DERIVED_PARTS as readonly string[]).includes(k));

/**
 * True when an operation composed `part` of this orientation entry from the
 * base composition (it is not an authored override). `derived: true` names
 * the position only (v5's form).
 */
export function isDerived(entry: Partial<OrientationLayout> | undefined, part: DerivedPart): boolean {
  const mark = entry?.derived;
  if (mark === undefined) return false;
  if (mark === true) return part === 'position';
  return mark[part] === true;
}

/**
 * The entry with `part` added to (`on`) or removed from its derived mark, in
 * the shortest form: no parts, no key; only the position, `true`; otherwise
 * an object of `true` values. Never `null`, `false` or `{}`. A retired part
 * (v6–v14's `enterAnimation`, `exitAnimation`, `delay`, `hold`) is dropped.
 * Returns the same entry when the mark does not change.
 */
export function withDerived<E extends Partial<OrientationLayout>>(entry: E, part: DerivedPart, on: boolean): E {
  if (isDerived(entry, part) === on && !hasRetiredParts(entry.derived)) return entry;
  const parts = DERIVED_PARTS.filter((p) => (p === part ? on : isDerived(entry, p)));
  const { derived: _old, ...rest } = entry;
  if (parts.length === 0) return rest as E;
  if (parts.length === 1 && parts[0] === 'position') return { ...rest, derived: true } as E;
  const mark: DerivedMark = {};
  for (const p of parts) mark[p] = true;
  return { ...rest, derived: mark } as E;
}

/**
 * Set a single position field for an orientation. In the basis it updates
 * that field of the basis position; elsewhere it stores only that field as
 * an override. The position is then authored, so the `position` part of the
 * derived mark (spec 021) is dropped; other parts stay.
 */
export function setPositionField(
  layout: ResponsiveLayout,
  orientation: Orientation,
  field: 'x' | 'y',
  value: number,
): ResponsiveLayout {
  const basis = getBasisOrientation(layout) ?? orientation;
  const entry = layout[orientation] ? withDerived(layout[orientation]!, 'position', false) : undefined;
  if (orientation === basis) {
    const basisPos = layout[orientation]?.position ?? DEFAULT_POSITION;
    return { ...layout, basis, [orientation]: { ...entry, position: { ...basisPos, [field]: value } } };
  }
  // Non-basis: store only the changed field.
  const existing = layout[orientation]?.position ?? {};
  return { ...layout, basis, [orientation]: { ...entry, position: { ...existing, [field]: value } as Position } };
}

/**
 * Set transform fields for an orientation: in the basis they update the basis
 * transform; elsewhere only the given fields are stored as overrides. A scale
 * or rotation written is authored, so that part of the derived mark is
 * dropped (`scale`, `rotation`); other parts stay.
 */
export function setOrientationTransform(
  layout: ResponsiveLayout,
  orientation: Orientation,
  transform: Partial<Transform>,
): ResponsiveLayout {
  const basis = getBasisOrientation(layout) ?? orientation;
  const existing = layout[orientation]?.transform ?? {};
  let entry: Partial<OrientationLayout> = { ...layout[orientation], transform: { ...existing, ...transform } as Transform };
  if (transform.scale !== undefined) entry = withDerived(entry, 'scale', false);
  if (transform.rotation !== undefined) entry = withDerived(entry, 'rotation', false);
  return { ...layout, basis, [orientation]: entry as OrientationLayout };
}

/**
 * Remove a single field override for a non-basis orientation. A position
 * left with neither x nor y is removed whole (whatever `anchor` or `unit` it
 * still stores) with the `position` part of the derived mark, so the
 * orientation follows the basis position again; removing the scale or the
 * rotation drops that part; a transform left empty is removed; an entry left empty is
 * removed.
 */
export function removeFieldOverride(
  layout: ResponsiveLayout,
  orientation: Orientation,
  field: OverridableField,
): ResponsiveLayout {
  const basis = getBasisOrientation(layout);
  if (!basis || orientation === basis) return layout;
  const oriLayout = layout[orientation];
  if (!oriLayout) return layout;

  let next: OrientationLayout = { ...oriLayout };
  if (field === 'x' || field === 'y') {
    if (!next.position) return layout;
    const restPos: Partial<Position> = { ...next.position };
    delete restPos[field];
    if (restPos.x === undefined && restPos.y === undefined) {
      const { position: _p, ...rest } = withDerived(next, 'position', false);
      next = rest as OrientationLayout;
    } else {
      next = { ...next, position: restPos as Position };
    }
  } else {
    if (!next.transform) return layout;
    const { [field]: _drop, ...restTransform } = next.transform as Partial<Transform>;
    next = withDerived(next, field, false);
    if (Object.keys(restTransform).length === 0) {
      const { transform: _t, ...rest } = next;
      next = rest as OrientationLayout;
    } else {
      next = { ...next, transform: restTransform as Transform };
    }
  }

  if (Object.keys(next).length === 0) {
    const { [orientation]: _gone, ...rest } = layout;
    return rest as ResponsiveLayout;
  }
  return { ...layout, [orientation]: next };
}

/**
 * Write a sprite's at-rest flip (spec 038 D-2): one value for every
 * orientation, stored on the basis orientation's transform; `flipX` /
 * `flipY` is removed from every other orientation's transform override (a
 * transform left empty is removed, and an entry left empty with it). The
 * derived mark is untouched: a flip is not one of its parts.
 */
export function setAtRestFlip(
  layout: ResponsiveLayout | undefined,
  axis: 'x' | 'y',
  value: boolean,
): ResponsiveLayout {
  const field = axis === 'x' ? 'flipX' : 'flipY';
  const base: ResponsiveLayout = layout ?? {};
  const basis = getBasisOrientation(base) ?? 'portrait';
  let next: ResponsiveLayout = { ...base, basis };
  for (const o of ORIENTATIONS) {
    if (o === basis) continue;
    const entry = next[o];
    if (!entry?.transform || !(field in entry.transform)) continue;
    const { [field]: _drop, ...restTransform } = entry.transform as Partial<Transform>;
    let rest: Partial<OrientationLayout> = { ...entry };
    if (Object.keys(restTransform).length === 0) delete rest.transform;
    else rest = { ...rest, transform: restTransform as Transform };
    if (Object.keys(rest).length === 0) {
      const { [o]: _gone, ...others } = next;
      next = others as ResponsiveLayout;
    } else {
      next = { ...next, [o]: rest as OrientationLayout };
    }
  }
  const basisEntry = next[basis];
  const transform = { ...basisEntry?.transform, [field]: value } as Transform;
  return { ...next, [basis]: { ...basisEntry, transform } as OrientationLayout };
}
